import React, { useState, useEffect } from "react";
import { Spin, Tag, Tooltip, message } from "antd";
import { RobotOutlined, CopyOutlined, RedoOutlined, WarningOutlined } from "@ant-design/icons";
import axios from "axios";
import { aiMediaAnalysisByMessageApi, AI_MEDIA_ANALYZE_API } from "../ai/constants";
import { API_DOMAIN } from "../constants";

function copyText(text) {
  if (!text) return;
  navigator.clipboard.writeText(text);
  message.success("Copied to clipboard");
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

// Same download endpoint the existing per-bubble download button
// (handleDownload in MessageParts.js) uses — the backend wants the actual
// image bytes (as a base64 data URL) rather than a signed URL it would have
// to fetch itself, so this reuses that pipeline instead of _resolveMediaUrl.
async function downloadWatiImageAsDataUrl(rawUrl) {
  const res = await axios.post(
    API_DOMAIN + "/wati_messages/download_wati_media_file",
    { file_name: rawUrl },
    { headers: { accessToken: localStorage.getItem("accessToken") }, responseType: "blob" }
  );
  return blobToDataUrl(res.data);
}

// Shared state/logic behind the small overlay button (ImageAiAnalysisButton)
// and the inline result panel (ImageAiAnalysisResult) rendered in the
// bubble — split so the button can sit absolutely-positioned over the image
// while the result renders in normal document flow below it, in-bubble like
// the translation feature (no modal — copy/paste and reading alongside the
// customer drawer need the text to just sit in the page).
export function useImageAiAnalysis({ messageId, rawUrl, conversationId, customerPhone, enabled }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState(null);

  // Passive existence check so the button reflects an already-analyzed
  // image (globally cached by wati_message_id) without requiring a click —
  // and, per the backend's recommended flow, without re-downloading the
  // image at all when a completed result is already cached.
  useEffect(() => {
    if (!enabled || !messageId) return;
    let cancelled = false;
    axios.get(aiMediaAnalysisByMessageApi(messageId), { headers: { accessToken: localStorage.getItem("accessToken") } })
      .then(res => { if (!cancelled && res.data?.success) setAnalysis(res.data.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [enabled, messageId]);

  const runAnalysis = async (forceReanalyze = false) => {
    if (!messageId || !rawUrl || loading) return;
    setLoading(true);
    setError(null);
    let mediaDataUrl;
    try {
      mediaDataUrl = await downloadWatiImageAsDataUrl(rawUrl);
    } catch {
      setError("Failed to download the image for analysis.");
      message.error("Failed to download the image for analysis.");
      setLoading(false);
      return;
    }
    try {
      const res = await axios.post(AI_MEDIA_ANALYZE_API, {
        wati_message_id: messageId,
        media_url: mediaDataUrl,
        conversation_id: conversationId || undefined,
        customer_phone: customerPhone || undefined,
        force_reanalyze: forceReanalyze,
      }, { headers: { accessToken: localStorage.getItem("accessToken") } });
      if (res.data?.success) {
        setAnalysis(res.data.data);
      } else {
        setError(res.data?.message || "AI analysis failed.");
        message.error(res.data?.message || "AI analysis failed.");
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        message.warning(e.response?.data?.message || "This image is already being analyzed — try again shortly.");
        if (e.response?.data?.data) setAnalysis(e.response.data.data);
      } else if (status === 503) {
        setError("AI image analysis is currently disabled.");
        message.error("AI image analysis is currently disabled.");
      } else if (status === 413) {
        setError("Image too large to analyze.");
        message.error("Image too large to analyze.");
      } else {
        const msg = e?.response?.data?.message || "AI analysis failed.";
        setError(msg);
        message.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    if (analysis?.status === "completed" || error) {
      setVisible(v => !v);
      return;
    }
    if (visible) {
      setVisible(false);
      return;
    }
    setVisible(true);
    runAnalysis(false);
  };

  return { analysis, loading, visible, error, toggle, runAnalysis };
}

export function ImageAiAnalysisButton({ state }) {
  const { analysis, loading, visible, toggle } = state;
  const hasResult = analysis?.status === "completed";
  const isFailed = analysis?.status === "failed";

  return (
    <Tooltip title={visible ? "Hide AI analysis" : hasResult ? "View AI analysis" : isFailed ? "AI analysis failed — click to retry" : "AI Analysis"}>
      <span
        onClick={(e) => { e.stopPropagation(); toggle(); }}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 26, height: 26, borderRadius: "50%",
          background: visible ? "#00a884" : hasResult ? "rgba(82,196,26,0.85)" : isFailed ? "rgba(255,77,79,0.85)" : "rgba(0,0,0,0.45)",
          color: "#fff", flexShrink: 0, cursor: loading ? "not-allowed" : "pointer",
        }}
      >
        {loading ? <Spin size="small" style={{ fontSize: 11 }} /> : <RobotOutlined style={{ fontSize: 13 }} />}
      </span>
    </Tooltip>
  );
}

// Renders in-flow below the image (same spot/style family as the
// translation caption in ChatPanelMessages.js) so the text is plain,
// selectable and copyable — no modal to close before pasting into the
// customer drawer or a supplier chat.
export function ImageAiAnalysisResult({ state, isDark }) {
  const { analysis, loading, visible, error, runAnalysis } = state;
  if (!visible) return null;

  const isFailed = analysis?.status === "failed";
  const structured = analysis?.structured_result || null;
  const labelColor = isDark ? "#8696a0" : "#667781";
  const textColor = isDark ? "#e9edef" : "#111b21";

  return (
    <div
      onClick={e => e.stopPropagation()}
      style={{
        marginTop: 4, paddingTop: 6, borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
        width: 0, minWidth: "100%", boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: labelColor, textTransform: "uppercase", letterSpacing: 0.4, display: "flex", alignItems: "center", gap: 4 }}>
          <RobotOutlined /> AI Analysis
        </span>
        {!loading && (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Tooltip title="Reanalyze">
              <RedoOutlined style={{ fontSize: 11, color: labelColor, cursor: "pointer" }} onClick={() => runAnalysis(true)} />
            </Tooltip>
          </span>
        )}
      </div>

      {loading ? (
        <Spin size="small" />
      ) : error || isFailed ? (
        <div style={{ fontSize: 12, color: "#ff4d4f" }}>{analysis?.error_message || error || "Analysis failed."}</div>
      ) : (
        <>
          {analysis?.result_type === "text_extraction" && analysis?.extracted_text && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 10, color: labelColor }}>Extracted Text</span>
                <CopyOutlined style={{ fontSize: 11, cursor: "pointer", color: labelColor }} onClick={() => copyText(analysis.extracted_text)} />
              </div>
              <div dir="auto" style={{ fontSize: 12, color: textColor, whiteSpace: "pre-wrap", wordBreak: "break-word", userSelect: "text" }}>
                {analysis.extracted_text}
              </div>
            </div>
          )}
          {analysis?.description && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 10, color: labelColor }}>Description</span>
                <CopyOutlined style={{ fontSize: 11, cursor: "pointer", color: labelColor }} onClick={() => copyText(analysis.description)} />
              </div>
              <div dir="auto" style={{ fontSize: 12, color: textColor, whiteSpace: "pre-wrap", wordBreak: "break-word", userSelect: "text" }}>
                {analysis.description}
              </div>
            </div>
          )}
          {analysis?.confidence_score != null && (
            <div style={{ fontSize: 10, color: labelColor, marginBottom: 4 }}>
              AI confidence: {analysis.confidence_score}% <span style={{ fontStyle: "italic" }}>(self-assessed, not guaranteed)</span>
            </div>
          )}
          {structured?.detected_language && (
            <div style={{ fontSize: 10, color: labelColor, marginBottom: 2 }}>Detected language: {structured.detected_language}</div>
          )}
          {structured?.warnings?.length > 0 && (
            <div style={{ fontSize: 10, color: "#faad14", marginBottom: 2 }}>
              <WarningOutlined /> {structured.warnings.join(", ")}
            </div>
          )}
          {analysis?.cached && <Tag style={{ fontSize: 10, marginTop: 2 }}>Cached result</Tag>}
        </>
      )}
    </div>
  );
}
